import React, { useRef } from 'react';
import { Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { useReactToPrint } from 'react-to-print';

export interface CardProps {
  title: string;
  content: JSX.Element;
  backgroundColor?: string;
  cta?: CtaProps;
  minHeight?: number;
  printable?: boolean;
  cardAction?: JSX.Element;
}

export type CtaProps = {
  btnId?: string;
  icon?: JSX.Element;
  text?: string;
  link?: string;
  func?: () => void;
  subText?: string;
  subTextLink?: string | undefined;
  subTextFunction?: () => void;
};

export const RootineCard = (props: CardProps): JSX.Element => {
  const componentRef = useRef(null);
  const printableContent = React.useCallback(() => {
    return componentRef.current;
  }, [componentRef.current]);

  const handlePrint = useReactToPrint({
    content: printableContent,
    documentTitle: 'rootine_nutrient_data',
    removeAfterPrint: true,
  });

  return (
    <>
      <Grid
        container
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Grid item>
          <Typography
            variant="light"
            color="text.secondary"
            sx={{
              textAlign: 'left',
              fontSize: { xs: 14, sm: 16 },
              letterSpacing: 2,
            }}
          >
            {props.title}
          </Typography>
        </Grid>
        {props.printable && (
          <Grid item>
            <Typography
              onClick={handlePrint}
              variant="light"
              sx={{
                color: 'info.main',
                fontSize: 12,
                letterSpacing: 2,
                cursor: 'pointer',
              }}
            >
              PRINT
            </Typography>
          </Grid>
        )}
        {props.cardAction && <Grid item>{props.cardAction}</Grid>}
      </Grid>
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          mt: 0.5,
          minHeight: props.minHeight ? props.minHeight : 300,
          boxShadow: '0px 1px 16px rgba(137, 137, 137, 0.25)',
          display: 'grid',
          backgroundColor: props.backgroundColor || 'inherit',
        }}
      >
        <CardContent ref={componentRef}>{props.content}</CardContent>
        {props.cta?.text && (
          <Grid container>
            <Grid
              container
              item
              xs={12}
              justifyContent="center"
              sx={{ px: 4, mb: props.cta.icon ? 2 : 0 }}
            >
              <Button
                variant="contained"
                id={props.cta?.btnId}
                disabled={!props.cta.func && !props.cta.link}
                sx={{
                  borderRadius: 0,
                  display: 'flex',
                  maxHeight: '44px',
                  backgroundColor: 'info.main',
                  color: '#fff',
                  '&.Mui-disabled': {
                    backgroundColor: 'info.main',
                    color: '#fff',
                  },
                }}
                fullWidth
                href={props.cta.link ? props.cta.link : undefined}
                onClick={props.cta.func ? props.cta.func : undefined}
              >
                <Grid container item xs={12} justifyContent="center">
                  <Grid item sx={{ pr: 1, pt: 0.5 }}>
                    {props.cta.icon && props.cta.icon}
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="medium"
                      sx={{
                        fontSize: { xs: 14, sm: 18 },
                        textAlign: 'center',
                      }}
                    >
                      {props.cta.text}
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            </Grid>
            {props.cta.subText && (
              <Grid container item xs={12} justifyContent="center">
                <Typography
                  variant="light"
                  sx={{
                    fontSize: { xs: 12 },
                    paddingTop: 2,
                    textAlign: 'center',
                    textDecoration:
                      props.cta?.subTextLink || props.cta?.subTextFunction
                        ? 'underline'
                        : 'none',
                    cursor:
                      props.cta?.subTextLink || props.cta?.subTextFunction
                        ? 'pointer'
                        : 'auto',
                  }}
                  onClick={
                    props.cta.subTextFunction && props.cta.subTextFunction
                  }
                >
                  {props.cta.subText}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Card>
    </>
  );
};
